/**
 * Utility functions for Simulator page
 */

import { faker } from "@faker-js/faker"

/**
 * Generate a random fake full name for a test contact
 */
export function generateFakeName(): string {
  return faker.person.fullName()
}

/**
 * Parse CSV string into test cases.
 * Format: name,message or name,message,system_prompt
 * Or just one column (message per line) for auto-generated names.
 */
export function parseCSV(csv: string): Array<{ label: string; message: string; systemPrompt?: string }> {
  const lines = csv.trim().split("\n")
  if (lines.length === 0) return []

  // Skip header row if present
  const hasHeader = lines[0].toLowerCase().includes("name") || lines[0].toLowerCase().includes("phone")
  const dataLines = hasHeader ? lines.slice(1) : lines

  return dataLines
    .map((line) => {
      const parts = line.split(",").map((p) => p.trim())
      if (parts.length === 0 || (parts.length === 1 && !parts[0])) return null
      // Single column: treat as message only, name will be auto-generated by caller
      if (parts.length === 1) {
        return {
          label: generateFakeName(),
          message: parts[0],
          systemPrompt: undefined,
        }
      }
      return {
        label: parts[0],
        message: parts[1],
        systemPrompt: parts[2] || undefined,
      }
    })
    .filter((item): item is NonNullable<typeof item> => item !== null)
}

/**
 * Generate CSV from test cases
 */
export function generateCSV(testCases: Array<{ label: string; message: string; systemPrompt?: string }>): string {
  const header = "Name,Message,System Prompt (Optional)"
  const rows = testCases.map((tc) => {
    const prompt = tc.systemPrompt || ""
    return `"${tc.label}","${tc.message}","${prompt}"`
  })

  return [header, ...rows].join("\n")
}

/**
 * Delay helper
 */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Extract quick-reply options encoded as [bracketed] text in a message.
 * Mirrors the parsing used by the agent flow chat panel so the simulator
 * surfaces the same quick-reply buttons. Returns the list of options.
 */
export function extractQuickReplies(text: string | null | undefined): string[] {
  if (!text) return []
  const options: string[] = []
  const regex = /\[([^\]]+)\]/g
  let match: RegExpExecArray | null
  while ((match = regex.exec(text)) !== null) {
    const opt = match[1].trim()
    if (opt) options.push(opt)
  }
  return options
}
